import { useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { Sensors, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { adAccountsService, AdAccount, FbPixel } from '../../services/ad-accounts.service';
import { useUiStore } from '../../stores/ui.store';

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function PixelsPage() {
  const { selectedAdAccountId, setSelectedAdAccountId } = useUiStore();

  const { data: accountsResult, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['adAccounts'],
    queryFn: () => adAccountsService.query({ limit: 100 }),
  });

  const adAccounts: AdAccount[] = accountsResult?.data || [];

  // Auto-select first account
  useEffect(() => {
    if (!selectedAdAccountId && adAccounts.length > 0) {
      setSelectedAdAccountId(adAccounts[0].id);
    }
  }, [adAccounts, selectedAdAccountId, setSelectedAdAccountId]);

  const { data: pixels, isLoading: isLoadingPixels, error } = useQuery({
    queryKey: ['pixels', selectedAdAccountId],
    queryFn: () => adAccountsService.listPixels(selectedAdAccountId!),
    enabled: !!selectedAdAccountId,
  });

  if (isLoadingAccounts) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (adAccounts.length === 0) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Pixels & Data Sources
        </Typography>
        <Alert severity="warning">
          No ad accounts connected. Connect an ad account first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Pixels & Data Sources
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Ad Account</InputLabel>
          <Select
            value={selectedAdAccountId || ''}
            onChange={(e) => setSelectedAdAccountId(e.target.value)}
            label="Ad Account"
          >
            {adAccounts.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pixels. The ad account token may not have the required permissions.
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pixel</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingPixels ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !pixels || pixels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Sensors sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        No pixels found for this ad account.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create a pixel in Meta Events Manager, then it will appear here.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pixels.map((pixel: FbPixel) => {
                  const isHealthy = !pixel.is_unavailable && pixel.last_fired_time;
                  const isRecent = pixel.last_fired_time &&
                    (Date.now() - new Date(pixel.last_fired_time).getTime()) < 7 * 24 * 60 * 60 * 1000;

                  return (
                    <TableRow key={pixel.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Sensors color={isHealthy ? 'success' : 'disabled'} />
                          <Typography variant="body2" fontWeight={500}>
                            {pixel.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {pixel.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pixel.is_unavailable ? (
                          <Chip icon={<ErrorIcon />} label="Unavailable" color="error" size="small" />
                        ) : isRecent ? (
                          <Chip icon={<CheckCircle />} label="Active" color="success" size="small" />
                        ) : pixel.last_fired_time ? (
                          <Chip label="Inactive" color="warning" size="small" />
                        ) : (
                          <Chip label="No data" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={isRecent ? 'success.main' : 'text.secondary'}>
                          {timeAgo(pixel.last_fired_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {pixel.creation_time
                            ? new Date(pixel.creation_time).toLocaleDateString()
                            : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
